import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { uuidv7, type Page } from 'shared';
import { db, savePage, getMeta, trashPage } from '../db/db.js';
import { syncer } from '../sync/sync.js';
import { useApp } from '../state.js';
import TopBar, { SyncBadge } from '../ui/TopBar.js';

export default function NotebookView(): JSX.Element {
  const { notebookId = '' } = useParams();
  const navigate = useNavigate();
  const { settings, showToast } = useApp();
  const [confirmPage, setConfirmPage] = useState<Page | null>(null);

  const notebook = useLiveQuery(() => db.notebooks.get(notebookId), [notebookId]);
  const pages = useLiveQuery(
    async () => (await db.pages.where('notebookId').equals(notebookId).toArray())
      .filter((p) => !p.deletedAt).sort((a, b) => a.sort - b.sort),
    [notebookId], [] as Page[],
  );
  const thumbs = useLiveQuery(async () => {
    const out: Record<string, string> = {};
    for (const p of pages) out[p.id] = await getMeta<string>(`thumb:${p.id}`, '');
    return out;
  }, [pages], {} as Record<string, string>);

  async function createPage(): Promise<void> {
    const now = Date.now();
    const last = pages[pages.length - 1];
    const page: Page = {
      id: uuidv7(),
      notebookId,
      title: '',
      pageDate: new Date().toLocaleDateString('en-CA', { timeZone: settings?.timezone ?? 'America/Guatemala' }),
      // Herda o papel da página anterior: trocar toda vez seria atrito bobo.
      paper: last?.paper ?? settings?.defaultPaper ?? 'ruled',
      defaultProject: last?.defaultProject ?? null,
      sort: pages.length,
      createdAt: now,
      updatedAt: now,
    };
    await savePage(page);
    syncer.schedule(500);
    navigate(`/p/${page.id}`);
  }

  return (
    <>
      <TopBar title={notebook?.title ?? 'Caderno'} back="/">
        <SyncBadge />
        <button className="primary" onClick={createPage}>+ Nova página</button>
      </TopBar>
      <div className="content">
        <div className="grid">
          {pages.map((p) => (
            <div className="card notebook-card" key={p.id}>
              <button className="notebook-open" onClick={() => navigate(`/p/${p.id}`)}>
                {thumbs[p.id] ? <img src={thumbs[p.id]} alt="" /> : null}
                <h3>{p.title || 'Sem título'}</h3>
                <p>{p.pageDate}</p>
              </button>
              <button className="notebook-menu ghost" title="Apagar página" onClick={() => setConfirmPage(p)}>⋯</button>
            </div>
          ))}
          {!pages.length ? <p className="muted">Caderno vazio. Crie a primeira página.</p> : null}
        </div>
      </div>

      {confirmPage ? (
        <div className="sheet" onClick={(e) => { if (e.target === e.currentTarget) setConfirmPage(null); }}>
          <div>
            <h2 style={{ margin: 0 }}>Apagar &ldquo;{confirmPage.title || 'Sem título'}&rdquo;?</h2>
            <p className="muted small" style={{ margin: 0 }}>
              Fica na lixeira por 30 dias, com os traços e o texto. Dá para recuperar nesse prazo.
            </p>
            <div className="row wrap">
              <button onClick={() => setConfirmPage(null)}>Cancelar</button>
              <span className="spacer" />
              <button
                className="danger"
                onClick={() => {
                  const alvo = confirmPage;
                  setConfirmPage(null);
                  void trashPage(alvo).then(() => {
                    syncer.schedule(500);
                    showToast('Página na lixeira. Dá para recuperar por 30 dias.');
                  });
                }}
              >
                Mandar para a lixeira
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
